import { format, parseISO } from "date-fns";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { describeEvent } from "../../lib/schedule-history/describe-event";
import { useDateLocale } from "../../i18n/use-date-locale";
import { useHistoryStore } from "../../stores/history-store";
import { chronologicalCursor } from "../../lib/schedule-history/app-history-actions";
import { schedulePresets } from "../../lib/schedule-presets/schedule-presets";
import { describeMetadataAction } from "./describe-metadata-action";
import { builderSessionId } from "../../stores/metadata-builder-store";

export function HistoryPanel() {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const panelOpen = useHistoryStore(state => state.panelOpen);
  const events = useHistoryStore(state => state.events);
  const undoneIds = useHistoryStore(state => state.undoneIds);
  const cursor = chronologicalCursor(events, undoneIds);
  const jumpTo = useHistoryStore(state => state.jumpTo);
  const togglePanel = useHistoryStore(state => state.togglePanel);
  const currentRow = useRef<HTMLLIElement>(null);

  useEffect(() => {
    currentRow.current?.scrollIntoView({ block: "nearest" });
  }, [cursor, undoneIds, panelOpen]);

  if (!panelOpen) return null;

  const formatDay = (iso: string) => format(parseISO(iso), "EEE d", { locale });
  const rows = events.map((action, index) => {
    const { event } = action;
    const undone = undoneIds.includes(event.id);
    if (action.domain === "schedule") {
      const label = describeEvent(action.event, formatDay);
      return { id: event.id, target: index + 1, text: t(label.key, label.params), undone };
    }
    const label = describeMetadataAction(action.event, action.target, t);
    const preset = schedulePresets.find(preset => preset.id === action.sessionId);
    const sessionLabel = action.sessionId === builderSessionId ? t("builder.title") : preset?.label ?? action.sessionId;
    const text = t("history.metadataAction", { preset: sessionLabel, action: label });
    return { id: event.id, target: index + 1, text, undone };
  });
  const baseline = { id: "baseline", target: 0, text: t("history.baseline"), undone: false };

  return (
    <aside id="history-panel" aria-label={t("history.title")}
      className="fixed bottom-4 right-4 z-40 flex max-h-[min(24rem,60dvh)] w-64 flex-col rounded border border-neutral-200 bg-white shadow-lg">
      <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider">{t("history.title")}</h2>
        <Button variant="ghost" size="icon-xs" onClick={togglePanel} aria-label={t("history.hide")}>
          <X />
        </Button>
      </div>
      {cursor === null && <p className="border-b border-neutral-200 px-3 py-2 text-xs text-neutral-500">{t("history.scopedState")}</p>}
      <ol className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
        {[baseline, ...rows].map(row => {
          const isCurrent = row.target === cursor;
          const isUndone = row.undone;
          const title = `${row.text} — ${t(isUndone ? "history.undone" : "history.applied")}`;
          return (
            <li key={row.id} ref={isCurrent ? currentRow : undefined}>
              <button type="button" onClick={() => jumpTo(row.target)} aria-current={isCurrent}
                className={cn(
                  "w-full truncate rounded-sm px-2 py-1 text-left text-xs hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-neutral-900",
                  isCurrent && "bg-neutral-900 text-white hover:bg-neutral-800",
                  isUndone && "text-neutral-400 line-through",
                )}
                title={title}>
                {row.text}
              </button>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}
